import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { APIProvider, InfoWindow, Map, useMap } from '@vis.gl/react-google-maps';
import { MapPinned } from 'lucide-react';
import { Button, StatusPill } from '../../components';
import { formatCount } from '../../lib/format/money';
import type { LocaleActivityRow } from './api';
import { Balance, ofTarget } from './localeParts';

/**
 * Community Activation — activity density (FE plan D9: Google Maps).
 *
 * Each locale is drawn at its ACTUAL extent (from the same
 * `/admin/dashboard/locales` rows as the table):
 *   - polygon-defined locales as their boundary polygon;
 *   - centre + radius locales (including postal-code locales, whose centre is
 *     the ZIP's) as that circle.
 * A locale with neither is not drawn. Colour = activity level, fill strength =
 * dishes sold relative to the busiest locale; postal-code locales are a light,
 * translucent grey. Click a shape to zoom to its boundary and see its numbers,
 * with "Add to filter" to add it to the page's locale filter. The map itself is
 * never filtered — it shows every locale.
 *
 * Styling comes from the cloud style linked to `VITE_GOOGLE_MAPS_MAP_ID` (source:
 * `maps/community-activation.style.json`, loaded into Google Cloud by hand).
 * Without a Map ID the map renders Google's default look.
 */

const API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY ?? '';
const MAP_ID = import.meta.env.VITE_GOOGLE_MAPS_MAP_ID || undefined;

const LEVEL_COLOR: Record<LocaleActivityRow['activityLevel'], string> = {
  high: '#45A267',
  medium: '#B49E31',
  low: '#B23A2E',
};

export const POSTAL_CODE_TYPE = 'postalCode';
const POSTAL_GREY = '#A1A1A1';

/** Fill and stroke for a locale's shape. */
export function shapeStyle(row: LocaleActivityRow, maxSold: number) {
  if (row.type === POSTAL_CODE_TYPE) {
    return {
      fillColor: POSTAL_GREY,
      fillOpacity: 0.12,
      strokeColor: POSTAL_GREY,
      strokeOpacity: 0.45,
      strokeWeight: 1,
    };
  }
  const share = maxSold > 0 ? row.dishesSold / maxSold : 0;
  const color = LEVEL_COLOR[row.activityLevel] ?? LEVEL_COLOR.low;
  return {
    fillColor: color,
    fillOpacity: 0.18 + 0.47 * share,
    strokeColor: color,
    strokeOpacity: 0.9,
    strokeWeight: 1.5,
  };
}

/** How a locale is drawn: its polygon if it has one, else its circle; null if neither. */
export type LocaleShape =
  | { kind: 'polygon'; path: google.maps.LatLngLiteral[] }
  | { kind: 'circle'; center: google.maps.LatLngLiteral; radius: number };

export function localeShape(row: LocaleActivityRow): LocaleShape | null {
  if (row.polygon && row.polygon.length >= 3) {
    return { kind: 'polygon', path: row.polygon.map(([lon, lat]) => ({ lat, lng: lon })) };
  }
  if (typeof row.lat === 'number' && typeof row.lon === 'number' && row.radius && row.radius > 0) {
    return { kind: 'circle', center: { lat: row.lat, lng: row.lon }, radius: row.radius };
  }
  return null;
}

type Plotted = { row: LocaleActivityRow; shape: LocaleShape };

export function plottable(rows: LocaleActivityRow[]): Plotted[] {
  return rows.flatMap((row) => {
    const shape = localeShape(row);
    return shape ? [{ row, shape }] : [];
  });
}

/** A request to show one locale as if its shape were clicked. `nonce` lets the same locale be asked for twice. */
export interface MapFocus {
  localeId: string;
  nonce: number;
}

export function ActivityMap({
  rows,
  windowLabel = 'last 7 days',
  focus,
  filteredIds = [],
  onAddToFilter,
  onSelect,
  moveOnClick = true,
}: {
  rows: LocaleActivityRow[];
  /** The range the listed / sold counts cover, e.g. "last 7 days". */
  windowLabel?: string;
  focus?: MapFocus | null;
  /** The page's locale filter — the map never narrows to it, only shows who's in it. */
  filteredIds?: string[];
  onAddToFilter: (localeId: string) => void;
  /** Called whenever a locale is selected, from the map or a focus request. */
  onSelect?: (localeId: string) => void;
  /** Clicking a locale zooms and moves the map to it (default). Off: it only opens the card. */
  moveOnClick?: boolean;
}) {
  const shapes = useMemo(() => plottable(rows), [rows]);

  if (!API_KEY) {
    return <MapNotice>Map needs a Google Maps API key (VITE_GOOGLE_MAPS_API_KEY) for this environment.</MapNotice>;
  }
  if (!shapes.length) {
    return <MapNotice>No locales with a boundary or centre and radius to plot.</MapNotice>;
  }

  return (
    <APIProvider apiKey={API_KEY}>
      <Map
        className="h-full w-full"
        mapId={MAP_ID}
        defaultCenter={{ lat: 39.5, lng: -105.8 }}
        defaultZoom={8}
        gestureHandling="cooperative"
        disableDefaultUI
        zoomControl
        // Distance scale, bottom right (click it to switch miles / km).
        scaleControl
        clickableIcons={false}
      >
        <LocaleShapes
          shapes={shapes}
          windowLabel={windowLabel}
          focus={focus}
          filteredIds={filteredIds}
          onAddToFilter={onAddToFilter}
          onSelect={onSelect}
          moveOnClick={moveOnClick}
        />
      </Map>
    </APIProvider>
  );
}

interface Selected {
  row: LocaleActivityRow;
  at: google.maps.LatLngLiteral;
}

function LocaleShapes({
  shapes,
  windowLabel,
  focus,
  filteredIds,
  onAddToFilter,
  onSelect,
  moveOnClick,
}: {
  shapes: Plotted[];
  windowLabel: string;
  focus?: MapFocus | null;
  filteredIds: string[];
  onAddToFilter: (localeId: string) => void;
  onSelect?: (localeId: string) => void;
  moveOnClick: boolean;
}) {
  const map = useMap();
  // Read at click time, so flipping the toggle doesn't redraw every shape.
  const moveOnClickRef = useRef(moveOnClick);
  useEffect(() => {
    moveOnClickRef.current = moveOnClick;
  }, [moveOnClick]);
  const [selected, setSelected] = useState<Selected | null>(null);
  // Every selection — a shape click or a finder pick — opens the card and tells
  // the card's owner (which clears the finder). getCenter() can come back empty
  // when Google refused to initialise (e.g. a rejected key); do nothing then.
  const onSelectRef = useRef(onSelect);
  useEffect(() => {
    onSelectRef.current = onSelect;
  }, [onSelect]);
  const select = useCallback((row: LocaleActivityRow, bounds: google.maps.LatLngBounds) => {
    const center = bounds.getCenter?.();
    if (!center) return;
    setSelected({ row, at: center.toJSON() });
    onSelectRef.current?.(row.localeId);
  }, []);
  // Each drawn locale's bounds, so a focus request can do what a click does.
  const boundsById = useRef(new globalThis.Map<string, google.maps.LatLngBounds>());
  const maxSold = Math.max(0, ...shapes.map((s) => s.row.dishesSold));

  // Shapes are imperative google.maps objects; rebuilt when the rows change.
  useEffect(() => {
    if (!map) return;
    const all = new google.maps.LatLngBounds();
    boundsById.current.clear();
    const drawn = shapes.map(({ row, shape }) => {
      const style = shapeStyle(row, maxSold);
      const overlay =
        shape.kind === 'polygon'
          ? new google.maps.Polygon({ ...style, map, paths: shape.path, clickable: true })
          : new google.maps.Circle({ ...style, map, center: shape.center, radius: shape.radius, clickable: true });
      const bounds = boundsOf(overlay, shape);
      all.union(bounds);
      boundsById.current.set(row.localeId, bounds);
      // Drill in: zoom to this locale's actual extent and show its numbers —
      // or, with "Move when clicked" off, just show its numbers where it is.
      overlay.addListener('click', () => {
        if (moveOnClickRef.current) map.fitBounds(bounds, 48);
        select(row, bounds);
      });
      return overlay;
    });
    map.fitBounds(all, 48);
    return () => drawn.forEach((o) => o.setMap(null));
  }, [map, shapes, maxSold, select]);

  // A focus request (the header's locale finder) behaves exactly like a click.
  useEffect(() => {
    if (!map || !focus) return;
    const bounds = boundsById.current.get(focus.localeId);
    const hit = shapes.find((s) => s.row.localeId === focus.localeId);
    if (!bounds || !hit) return;
    map.fitBounds(bounds, 48);
    select(hit.row, bounds);
  }, [map, focus, shapes, select]);

  if (!selected) return null;
  const r = selected.row;
  return (
    <InfoWindow
      position={selected.at}
      onCloseClick={() => setSelected(null)}
      // The card pans the map to fit itself; that is movement too.
      disableAutoPan={!moveOnClick}
      // Don't move focus into the card: after a keyboard pick it lands on "Add to filter" with a focus ring.
      shouldFocus={false}
      headerContent={
        // Inline weight: Google's InfoWindow CSS overrides the header's font weight, so a class loses.
        <span className="font-heading text-[15px] text-brown-deep" style={{ fontWeight: 700 }}>
          {r.name}
        </span>
      }
    >
      <div className="flex min-w-[220px] flex-col gap-1.5 font-body text-[13px] text-ink">
        <div className="text-xs text-muted">
          {[r.city, r.stateProvince].filter(Boolean).join(', ')} · {ofTarget(r)}
        </div>
        <div>
          {r.type === POSTAL_CODE_TYPE ? (
            <StatusPill tone="neutral">Postal code only</StatusPill>
          ) : (
            <StatusPill tone="locale">Dish locale</StatusPill>
          )}
        </div>
        <div>
          {formatCount(r.chefCount)} chefs · {formatCount(r.eaterCount)} eaters
        </div>
        <div>
          {formatCount(r.dishesListed)} listed · {formatCount(r.dishesSold)} sold ({windowLabel})
        </div>
        <div>
          <Balance r={r} />
        </div>
        <div className="pt-1">
          {filteredIds.includes(r.localeId) ? (
            <Button size="xs" variant="ghost-dark" disabled>
              In the filter
            </Button>
          ) : (
            <Button size="xs" variant="ghost-dark" onClick={() => onAddToFilter(r.localeId)}>
              Add to filter
            </Button>
          )}
        </div>
      </div>
    </InfoWindow>
  );
}

function boundsOf(overlay: google.maps.Polygon | google.maps.Circle, shape: LocaleShape): google.maps.LatLngBounds {
  if (shape.kind === 'circle') return (overlay as google.maps.Circle).getBounds() ?? new google.maps.LatLngBounds();
  const b = new google.maps.LatLngBounds();
  shape.path.forEach((p) => b.extend(p));
  return b;
}

function MapNotice({ children }: { children: string }) {
  return (
    <div
      className="flex h-full flex-col items-center justify-center gap-2 p-4 text-center font-mono text-xs text-brown"
      style={{
        background:
          'repeating-linear-gradient(135deg,rgba(143,68,32,.06) 0 14px,rgba(143,68,32,.11) 14px 28px),var(--hp-cream-warm)',
      }}
    >
      <MapPinned size={30} aria-hidden="true" />
      {children}
    </div>
  );
}
